import React, { memo } from 'react';
import Box from '@mui/material/Box';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useSettingsContext } from 'src/components/settings';
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';

// Memo pour éviter les rerenders inutiles
const MemoNavVertical = memo(NavVertical);
const MemoNavMini = memo(NavMini);
const MemoNavHorizontal = memo(NavHorizontal);

// Composant nav stable
function DashboardNav({
  layout,
  lgUp,
  open,
  onClose,
}: {
  layout: string;
  lgUp: boolean;
  open: boolean;
  onClose: () => void;
}) {
  if (layout === 'horizontal') {
    return lgUp ? <MemoNavHorizontal /> : <MemoNavVertical openNav={open} onCloseNav={onClose} />;
  }

  if (layout === 'mini') {
    return lgUp ? <MemoNavMini /> : <MemoNavVertical openNav={open} onCloseNav={onClose} />;
  }

  return <MemoNavVertical openNav={open} onCloseNav={onClose} />;
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const settings = useSettingsContext();
  const lgUp = useResponsive('up', 'lg');
  const nav = useBoolean();

  const isMini = settings.themeLayout === 'mini';

  // Box sx stables
  const containerSx = React.useMemo(
    () => ({
      minHeight: 1,
      display: 'flex',
      flexDirection: { xs: 'column', lg: 'row' },
      ...(isMini && { backgroundColor: 'rgba(247, 247, 247, 1)' }),
    }),
    [isMini]
  );

  return (
    <Box sx={{height: '100%'}}>
      <Header onOpenNav={nav.onTrue} />
      <Box sx={containerSx}>
        <DashboardNav
          layout={settings.themeLayout}
          lgUp={lgUp}
          open={nav.value}
          onClose={nav.onFalse}
        />
        <Main>{children}</Main>
      </Box>
    </Box>
  );
}
